import { useMemo, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { AlertTriangle, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, CATEGORIES, CATEGORY_LABELS, imageUri, SIZES } from "@/lib/noleggio";

const units = (b) => SIZES.reduce((s, k) => s + (b.stock?.[k] ?? 0), 0);

/** Merges duplicate bikes of the same category: keeps the one linked to the sheet stock, copies data/photo from the chosen one, deletes the rest. */
export default function AdminMergeDuplicates({ bikes }) {
  const qc = useQueryClient();
  const groups = useMemo(() => CATEGORIES.map((c) => bikes.filter((b) => b.category === c)).filter((g) => g.length > 1), [bikes]);
  const [open, setOpen] = useState(false);
  const [pick, setPick] = useState({});
  const [busy, setBusy] = useState(false);
  if (!groups.length) return null;

  const choice = (g) => {
    const cat = g[0].category;
    const keep = g.reduce((a, b) => (units(b) > units(a) ? b : a), g[0]);
    const source = g.find((b) => b.id === pick[cat]) ?? g.find((b) => b.image_url) ?? g[0];
    return { keep, source };
  };

  const run = async () => {
    setBusy(true);
    try {
      for (const g of groups) {
        const { keep, source } = choice(g);
        await api(`/admin/bikes/${keep.id}`, { method: "PUT", body: {
          name: source.name, category: keep.category, description: source.description ?? "", description_en: source.description_en ?? "",
          image_url: source.image_url ?? "", price_day: source.price_day, specs: source.specs ?? {}, stock: keep.stock, model_year: source.model_year ?? "",
        } });
        for (const b of g) if (b.id !== keep.id) await api(`/admin/bikes/${b.id}`, { method: "DELETE" });
      }
      toast.success("Catalogo sistemato: una bici per modello");
      setOpen(false);
      qc.invalidateQueries();
    } catch (e) {
      toast.error(e.message);
      qc.invalidateQueries();
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <div data-testid="admin-duplicates-banner" className="mn-card p-4 mt-4 flex items-center gap-3 border-[1.5px] border-amber-300 bg-amber-50">
        <AlertTriangle className="shrink-0" color="#c97a00" />
        <p className="flex-1 text-sm"><b>Ci sono bici doppie</b> nello stesso tipo ({groups.map((g) => `${g.length} ${CATEGORY_LABELS[g[0].category]}`).join(", ")}). Puoi tenerne una per modello.</p>
        <button data-testid="admin-duplicates-open" className="mn-btn !min-h-[42px] text-sm" onClick={() => setOpen(true)}>Sistema</button>
      </div>
      {open && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-end md:items-center justify-center" onClick={() => !busy && setOpen(false)}>
          <div data-testid="admin-duplicates-dialog" onClick={(e) => e.stopPropagation()} className="bg-white w-full md:max-w-2xl max-h-[92vh] overflow-y-auto rounded-t-3xl md:rounded-3xl p-6 flex flex-col gap-4 mn-rise">
            <h2 className="text-xl font-extrabold">Una bici per modello</h2>
            <p className="text-sm text-[var(--text-2)]">Per ogni tipo resta la bici collegata alle quantità del foglio Google. Scegli da quale prendere <b>nome, foto, descrizione e caratteristiche</b>: le altre vengono eliminate. Le prenotazioni già fatte restano.</p>
            {groups.map((g) => {
              const cat = g[0].category;
              const { keep, source } = choice(g);
              return (
                <div key={cat} data-testid={`admin-duplicates-group-${cat}`} className="rounded-2xl border border-[var(--border)] p-3 flex flex-col gap-2">
                  <p className="font-extrabold">{CATEGORY_LABELS[cat]} <span className="text-xs font-semibold text-[var(--muted)]">· quantità da "{keep.name}" ({units(keep)} bici)</span></p>
                  {g.map((b) => (
                    <button key={b.id} data-testid={`admin-duplicates-pick-${b.id}`} onClick={() => setPick((p) => ({ ...p, [cat]: b.id }))}
                      className={`flex items-center gap-3 p-2 rounded-xl border-[1.5px] text-left transition-colors ${source.id === b.id ? "border-[var(--brand-primary)] bg-[var(--brand-tertiary)]" : "border-transparent hover:bg-[var(--surface-2)]"}`}>
                      {b.image_url ? <img src={imageUri(b.image_url)} alt="" className="w-20 h-14 rounded-lg object-cover bg-[var(--surface-3)]" /> : <div className="w-20 h-14 rounded-lg bg-[var(--surface-2)] text-[10px] text-[var(--muted)] flex items-center justify-center">senza foto</div>}
                      <div className="flex-1 min-w-0 text-sm">
                        <p className="font-bold truncate">{b.name}</p>
                        <p className="text-xs text-[var(--muted)] line-clamp-2">{b.description || "Nessuna descrizione"}</p>
                      </div>
                      <span className={`text-[11px] font-bold px-2 py-1 rounded-full shrink-0 ${source.id === b.id ? "bg-[var(--brand-primary)] text-white" : "bg-[var(--surface-2)] text-[var(--muted)]"}`}>{source.id === b.id ? "Usa questa" : "Elimina"}</span>
                    </button>
                  ))}
                </div>
              );
            })}
            <button data-testid="admin-duplicates-confirm" className="mn-btn" disabled={busy} onClick={run}>{busy && <Loader2 size={18} className="animate-spin" />} Sistema il catalogo</button>
            <button data-testid="admin-duplicates-cancel" className="mn-btn mn-btn-ghost" disabled={busy} onClick={() => setOpen(false)}>Annulla</button>
          </div>
        </div>
      )}
    </>
  );
}
